<template>
  <section>
    <v-container fluid>
      <v-row class="justify-between align-center">
        <v-col cols="auto">
          <v-btn class="accent shadow-4 px-8" large rounded @click="isFormOpen = true">
            Añadir Campaña
          </v-btn>
        </v-col>

        <v-col cols="auto" class="ml-auto">
          <v-text-field
            filled
            rounded
            dense
            v-model="search"
            append-icon="mdi-magnify"
            label="Buscar"
            single-line
            hide-details
          ></v-text-field>
        </v-col>
      </v-row>
    </v-container>

    <v-card class="shadow-1">
      <v-data-table
        :headers="headers"
        :items="tableList"
        :options.sync="tableOptions"
        :search="search"
        :loading="isLoading"
        :footer-props="{
          'items-per-page-options': [15, 30, 50, 100],
        }"
      >
        <template v-slot:[`item.responsible_id`]="{ item }">
          {{ ResponsibleById[item.responsible_id] ? ResponsibleById[item.responsible_id].name : "No data" }}
        </template>

        <template v-slot:[`item.specialty_id`]="{ item }">
          {{ SpecialtiesById[item.specialty_id] ? SpecialtiesById[item.specialty_id].name : "No data" }}
        </template>

        <template v-slot:[`item.status`]="{ item }">
          <v-chip color="transparent" small>
            <v-icon left small :color="getStatusColor(item.status)">mdi-radiobox-marked</v-icon>
            {{ item.status | statusName }}
          </v-chip>
        </template>

        <template v-slot:[`item.action`]="{ item }">
          <!-- ACCIÓN 1: RECICLAR NORMAL (Solo Superuser 1 y Supervisor 2) -->
          <v-hover v-slot:default="{ hover }" v-if="userRole === 1 || userRole === 2">
            <v-tooltip top>
              <template v-slot:activator="{ on, attrs }">
                <v-btn
                  text
                  icon
                  v-bind="attrs"
                  v-on="on"
                  :color="hover ? 'primary' : 'blue-grey'"
                  @click.stop="handleClickRecycle(item)"
                >
                  <v-icon>mdi-refresh-circle</v-icon>
                </v-btn>
              </template>
              <span>Reciclar citas no confirmadas</span>
            </v-tooltip>
          </v-hover>

          <!-- ACCIÓN 2: RECICLAR CON VOZ (NUEVA OPCIÓN) -->
          <v-hover v-slot:default="{ hover }" v-if="userRole === 1 || userRole === 2">
            <v-tooltip top>
              <template v-slot:activator="{ on, attrs }">
                <v-btn
                  text
                  icon
                  v-bind="attrs"
                  v-on="on"
                  :color="hover ? 'info' : 'blue-grey'"
                  @click.stop="handleClickRecycleVoice(item)"
                >
                  <v-icon>mdi-microphone-settings</v-icon>
                </v-btn>
              </template>
              <span>Reciclar con envío de voz</span>
            </v-tooltip>
          </v-hover>

          <v-hover v-slot:default="{ hover }">
            <v-btn text icon :color="hover ? 'error' : 'blue-grey'" @click.stop="handleClickDelete(item)">
              <v-icon>mdi-trash-can-outline</v-icon>
            </v-btn>
          </v-hover>
        </template>
      </v-data-table>
    </v-card>

    <campaigns-dialog :open="isFormOpen" :editData="itemToEdit" @dialog:close="closeForm"></campaigns-dialog>
    
    <!-- DIÁLOGO DE RECICLAJE NORMAL -->
    <v-dialog v-model="isRecycleOpen" max-width="500">
      <v-card class="pa-6 text-center" style="border-radius: 20px">
        <v-card-title class="justify-center primary--text headline font-weight-bold">
          Reciclar campaña
        </v-card-title>
        <v-card-text class="body-1 py-4 grey--text text--darken-2">
          ¿Está seguro que desea reciclar las citas no confirmadas de esta campaña?
          <br />
          <span class="font-weight-bold primary--text">{{ itemToEdit ? itemToEdit.name : '' }}</span>
        </v-card-text>
        <v-card-actions class="justify-center pb-4">
          <v-btn text large rounded color="grey darken-1" class="px-8 font-weight-bold" @click="isRecycleOpen = false">
            CANCELAR
          </v-btn>
          <v-btn large rounded color="accent" class="white--text px-10 font-weight-bold shadow-2" :loading="isLoading" @click="executeRecycle">
            RECICLAR
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- DIÁLOGO DE RECICLAJE CON VOZ -->
    <v-dialog v-model="isRecycleVoiceOpen" max-width="500">
      <v-card class="pa-6 text-center" style="border-radius: 20px">
        <v-card-title class="justify-center info--text headline font-weight-bold">
          Reciclar con voz
        </v-card-title>
        <v-card-text class="body-1 py-4 grey--text text--darken-2">
          ¿Desea reciclar las citas no confirmadas incluyendo el <b>envío de voz</b>?
          <br />
          <span class="font-weight-bold info--text">{{ itemToEdit ? itemToEdit.name : '' }}</span>
        </v-card-text>
        <v-card-actions class="justify-center pb-4">
          <v-btn text large rounded color="grey darken-1" class="px-8 font-weight-bold" @click="isRecycleVoiceOpen = false">
            CANCELAR
          </v-btn>
          <v-btn large rounded color="info" class="white--text px-10 font-weight-bold shadow-2" :loading="isLoading" @click="executeRecycleVoice">
            RECICLAR VOZ
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- DIÁLOGO DE ELIMINAR -->
    <v-dialog v-model="isDeleteOpen" max-width="500">
      <v-card class="pa-6 text-center" style="border-radius: 20px">
        <v-card-title class="justify-center primary--text headline font-weight-bold">
          Eliminar campaña
        </v-card-title>
        <v-card-text class="body-1 py-4 grey--text text--darken-2">
          ¿Está seguro que desea eliminar esta campaña? Esta acción no se puede deshacer.
          <br />
          <span class="font-weight-bold error--text">{{ itemToEdit ? itemToEdit.name : '' }}</span>
        </v-card-text>
        <v-card-actions class="justify-center pb-4">
          <v-btn text large rounded color="grey darken-1" class="px-8 font-weight-bold" @click="isDeleteOpen = false">
            CANCELAR
          </v-btn>
          <v-btn large rounded color="error" class="white--text px-10 font-weight-bold shadow-2" :loading="isLoading" @click="deleteItem">
            ELIMINAR
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

  </section>
</template>

<script>
import CampaignsDialog from "@/components/Appointments/CampaignsDialog.vue";
import { PbxHttpClient } from "@/services/httpclient";

export default {
  components: { CampaignsDialog },
  data() {
    return {
      isLoading: false,
      isFormOpen: false,
      isDeleteOpen: false,
      isRecycleOpen: false,
      isRecycleVoiceOpen: false, // Control para el nuevo diálogo
      search: "",
      headers: [
        { text: "Campaña", value: "name", class: "primary--text", width: "15%", align: "left" },
        { text: "Fecha de inicio", value: "start_date", class: "primary--text", width: "10%" },
        { text: "Fecha de fin", value: "end_date", class: "primary--text", width: "10%" },
        { text: "Total de citas", value: "total_appointments", class: "primary--text", width: "7%" },
        { text: "Especialidad", value: "specialty_id", class: "primary--text", width: "10%" },
        { text: "Reponsable", value: "responsible_id", class: "primary--text", width: "10%" },
        { text: "Estado", value: "status", class: "primary--text", width: "15%" },
        { text: "Acciones", value: "action", class: "primary--text", width: "10%", align: "right", sortable: false },
      ],
      tableOptions: { page: 1, itemsPerPage: 15 },
      tableList: [],
      responsibleList: [],
      specialtiesList: [],
      totalItems: 0,
      itemToEdit: null,
    };
  },

  computed: {
    userRole() {
      const userData = JSON.parse(localStorage.getItem("user"));
      return userData ? userData.role_id : null;
    },
    ResponsibleById() {
      return this.responsibleList.reduce((acc, el) => ({ ...acc, [el.id]: el }), {});
    },
    SpecialtiesById() {
      return this.specialtiesList.reduce((acc, el) => ({ ...acc, [el.id]: el }), {});
    },
  },

  watch: {
    tableOptions: {
      handler() { this.getData(); },
      deep: true,
    },
  },

  created() {
    this.getResponsible();
    this.getSpecialties();
  },

  filters: {
    statusName(val) {
      if (val === "pending") return "Pendiente";
      if (val === "completed") return "Completada";
      return "Pendiente";
    },
  },

  methods: {
    async getData() {
      this.isLoading = true;
      try {
        const { sortDesc, page, itemsPerPage } = this.tableOptions;
        const { data } = await PbxHttpClient.get("/module_appointment/send-appointments", {
          params: { page, itemPerPage: itemsPerPage, prioritySort: sortDesc },
        });
        this.tableList = data.data;
        this.totalItems = data.total;
      } catch (error) { console.log(error); }
      this.isLoading = false;
    },

    async getResponsible() {
      try {
        const { data } = await PbxHttpClient.get("/module_appointment/responsibles_select");
        this.responsibleList = data;
      } catch (error) { console.log(error); }
    },

    async getSpecialties() {
      try {
        const { data } = await PbxHttpClient.get("/module_appointment/specialties_select");
        this.specialtiesList = data;
      } catch (error) { console.log(error); }
    },

    closeForm(item = false) {
      this.isFormOpen = false;
      if (item) {
        if (this.itemToEdit) this.tableList = this.tableList.map((el) => (el.id === item.id ? item : el));
        else this.tableList = [...this.tableList, item];
      }
      this.itemToEdit = null;
    },

    handleClickRecycle(item) {
      this.itemToEdit = item;
      this.isRecycleOpen = true;
    },

    async executeRecycle() {
      this.isLoading = true;
      try {
        await PbxHttpClient.get(`/module_appointment/campaigns/reset-unconfirmed/${this.itemToEdit.id}`);
        this.$snotify.success("Campaña reciclada correctamente", "¡Hecho!");
        this.isRecycleOpen = false;
        this.getData();
      } catch (error) {
        this.$snotify.error("Hubo un error reciclando la campaña", "¡Error!");
      }
      this.isLoading = false;
    },

    // --- NUEVOS MÉTODOS PARA RECICLAJE CON VOZ ---
    handleClickRecycleVoice(item) {
      this.itemToEdit = item;
      this.isRecycleVoiceOpen = true;
    },

    async executeRecycleVoice() {
      this.isLoading = true;
      try {
        // Se añade el parámetro ?sent_voice=1 al final de la URL
        await PbxHttpClient.get(`/module_appointment/campaigns/reset-unconfirmed/${this.itemToEdit.id}?sent_voice=1`);
        this.$snotify.success("Campaña reciclada con voz correctamente", "¡Hecho!");
        this.isRecycleVoiceOpen = false;
        this.getData();
      } catch (error) {
        this.$snotify.error("Hubo un error reciclando con voz", "¡Error!");
      }
      this.isLoading = false;
    },
    // --------------------------------------------

    handleClickDelete(item) {
      this.itemToEdit = item;
      this.isDeleteOpen = true;
    },

    async deleteItem() {
      this.isLoading = true;
      try {
        await PbxHttpClient.delete(`/module_appointment/send-appointments/${this.itemToEdit.id}`);
        this.$snotify.success("Campaña eliminada con éxito", "¡Hecho!");
        this.tableList = this.tableList.filter((el) => el.id !== this.itemToEdit.id);
        this.isDeleteOpen = false;
      } catch (error) {
        this.$snotify.error("Hubo un error eliminando la Campaña", "¡Error!");
      }
      this.isLoading = false;
    },

    getStatusColor(status) {
      switch (status) {
        case "completed": return "success";
        case "pending": return "light-blue";
        case "canceled": return "error";
      }
      return "light-blue";
    },
  },
};
</script>
